import React from 'react';
import { Mail, Phone, MapPin, Twitter, Linkedin, Github } from 'lucide-react';
import Logo from './Logo';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer id="contact" className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex items-center gap-3 mb-4 hover:opacity-80 transition-opacity cursor-pointer group"
              aria-label="Go to home"
            >
              <Logo size="md" variant="on-dark" />
              <div className="flex flex-col">
                <span className="text-xl font-bold group-hover:text-blue-200 transition-colors">Security Guardian</span>
                <span className="text-sm text-gray-400">by Brain Station 23</span>
              </div>
            </button>
            <p className="text-gray-300 mb-4 leading-relaxed">
              Proactive AWS security management that transforms reactive operations 
              into intelligent, automated protection systems.
            </p>
            <p className="text-gray-400 text-sm mb-6">
              A product of Brain Station 23 - Bangladesh's leading software development company 
              with 15+ years of excellence in digital innovation.
            </p>
            <div className="flex space-x-4">
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                <Twitter className="h-5 w-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                <Linkedin className="h-5 w-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                <Github className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Product */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Product</h3>
            <ul className="space-y-3">
              <li><a href="#features" className="text-gray-300 hover:text-white transition-colors">Features</a></li>
              <li><a href="#pricing" className="text-gray-300 hover:text-white transition-colors">Pricing</a></li>
              <li><a href="#" className="text-gray-300 hover:text-white transition-colors">Security</a></li>
              <li><a href="#" className="text-gray-300 hover:text-white transition-colors">Compliance</a></li>
              <li><a href="#" className="text-gray-300 hover:text-white transition-colors">Integrations</a></li>
              <li><a href="#" className="text-gray-300 hover:text-white transition-colors">API Documentation</a></li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Resources</h3>
            <ul className="space-y-3">
              <li><a href="#" className="text-gray-300 hover:text-white transition-colors">Documentation</a></li>
              <li><a href="#" className="text-gray-300 hover:text-white transition-colors">Blog</a></li>
              <li><a href="#" className="text-gray-300 hover:text-white transition-colors">Case Studies</a></li>
              <li><a href="#" className="text-gray-300 hover:text-white transition-colors">Webinars</a></li>
              <li><a href="#" className="text-gray-300 hover:text-white transition-colors">AWS Security Best Practices</a></li>
              <li><a href="#" className="text-gray-300 hover:text-white transition-colors">Support Center</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Contact Brain Station 23</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-gray-400" />
                <div className="text-gray-300">
                  <div>aws-security@brainstation-23.com</div>
                  <div className="text-sm text-gray-400">info@brainstation-23.com</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="h-5 w-5 text-gray-400" />
                <span className="text-gray-300">+880 2 222 283 106</span>
              </div>
              <div className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-gray-400 mt-1" />
                <div className="text-gray-300">
                  <div>8th Floor, Plot 2, Road 144</div>
                  <div>Gulshan Avenue, Dhaka 1212</div>
                  <div>Bangladesh</div>
                </div>
              </div>
            </div>

            {/* Newsletter Signup */}
            <div className="mt-8">
              <h4 className="text-md font-semibold mb-4">Stay Updated</h4>
              <div className="flex">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-l-lg focus:outline-none focus:border-primary-500 text-white"
                />
                <button className="bg-primary-600 px-4 py-2 rounded-r-lg hover:bg-primary-700 transition-colors">
                  Subscribe
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-400 text-sm mb-4 md:mb-0">
              © {currentYear} Brain Station 23 Limited. AWS Security Guardian - All rights reserved.
            </div>
            <div className="flex space-x-6 text-sm">
              <a href="#" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">Terms of Service</a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">Cookie Policy</a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">GDPR</a>
            </div>
          </div>
        </div>

        {/* AWS Marketplace Badge */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-gray-800 px-4 py-2 rounded-lg">
            <svg className="w-6 h-6 text-orange-400" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6.763 10.036c.024-.048.05-.094.08-.138L9.15 5.74c.094-.137.248-.223.415-.223.167 0 .32.086.415.223l2.306 4.158c.03.044.056.09.08.138.048.095.073.2.073.309v4.516c0 .381-.31.691-.691.691H7.454c-.381 0-.691-.31-.691-.691v-4.516c0-.109.025-.214.073-.309zm7.781 3.273c0 .381.31.691.691.691h2.765c.381 0 .691-.31.691-.691V8.793c0-.109-.025-.214-.073-.309-.024-.048-.05-.094-.08-.138L16.232 4.188c-.094-.137-.248-.223-.415-.223-.167 0-.32.086-.415.223L13.096 8.346c-.03.044-.056.09-.08.138-.048.095-.073.2-.073.309v4.516z"/>
            </svg>
            <span className="text-gray-300 text-sm">Available on AWS Marketplace</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
